import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import { FinancingApi, LoanApplicationRead, LoanStatus2 } from '@servicetitan/financing-api-client';
import { useLoansWithNoJob } from '@servicetitan/financing-application-shared';
import { redirectToPostApplication } from '@servicetitan/financing-utils-shared';

export interface UseApprovedLoanBannerParams {
    tenantId: string;
    customerId: string;
    buId: string;
    currentJobId: string;
    showTechnicianLoanBanner: boolean;
}

export interface UseApprovedLoanBannerResult {
    approvedLoan: LoanApplicationRead | null;
    shouldShowBanner: boolean;
    handleContinueClick: () => Promise<void>;
    isLoading: boolean;
}

export const useApprovedLoanBanner = ({
    tenantId,
    customerId,
    buId,
    currentJobId,
    showTechnicianLoanBanner,
}: UseApprovedLoanBannerParams): UseApprovedLoanBannerResult => {
    const [apiClient] = useDependencies(FinancingApi);
    const [isNavigating, setIsNavigating] = useState(false);

    // Fetch loans with no job for GoodLeap
    const { data: loansData } = useLoansWithNoJob(tenantId, customerId, buId, 'GoodLeap');

    // Find the most recent approved loan
    const approvedLoan = useMemo(() => {
        if (!loansData?.data) {
            return null;
        }

        const approvedLoans = loansData.data
            .filter((loan: LoanApplicationRead) => loan.status === LoanStatus2.Approved)
            .sort((a: LoanApplicationRead, b: LoanApplicationRead) => {
                const dateA = new Date(a.appliedOn).getTime();
                const dateB = new Date(b.appliedOn).getTime();
                return dateB - dateA; // Sort descending (most recent first)
            });

        return approvedLoans.length > 0 ? approvedLoans[0] : null;
    }, [loansData]);

    // Mutation to update loan with jobId
    const updateLoanMutation = useMutation({
        mutationFn: (loanId: string) =>
            apiClient.loansUpdateLoanApplication({ id: loanId, isError: false }, [
                { op: 'replace', path: '/JobId', value: currentJobId },
                { op: 'replace', path: '/BuId', value: buId },
            ]),
        onSuccess: () => {
            setIsNavigating(true);
            // Navigate to technician-post-application
            if (approvedLoan?.id) {
                redirectToPostApplication(approvedLoan.id);
            }
        },
    });

    const handleContinueClick = async () => {
        if (approvedLoan?.id) {
            await updateLoanMutation.mutateAsync(approvedLoan.id);
        }
    };

    // Determine if banner should be shown
    const shouldShowBanner = showTechnicianLoanBanner && approvedLoan !== null;

    return {
        approvedLoan,
        shouldShowBanner,
        handleContinueClick,
        isLoading: updateLoanMutation.isPending || isNavigating,
    };
};
